/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useQuery } from '@apollo/client';

import path from 'js/lib/path';

import { Button, Typography2 } from '@coursera/cds-core';

import { isWeekLandingEnabled } from 'bundles/enroll-course/utils/featureGates';
import CourseEnrolledQuery from 'bundles/enroll-course/utils/queries/CourseEnrolledQuery.graphql';
import type {
  CourseEnrolledQueryQuery as CourseEnrolledQueryQueryData,
  CourseEnrolledQueryQueryVariables,
} from 'bundles/enroll-course/utils/queries/__generated__/CourseEnrolledQuery';
import { useCourseEnrollModalData } from 'bundles/enroll/data/usePageData';

import _t from 'i18n!nls/enroll-course';

import 'css!./__styles__/CourseEnrollmentConfirmation';

type PropsFromCaller = {
  courseId: string;
  showCdsDesign?: boolean;
  children?: React.JSX.Element;
};

const styles = {
  actionContainer: css`
    margin: 48px 0;
  `,
};

const CourseEnrollmentConfirmation = ({ courseId, showCdsDesign, children }: PropsFromCaller) => {
  const { courseTypeMetadataWithVersion } = useCourseEnrollModalData();

  const { data: courseEnrolledData } = useQuery<CourseEnrolledQueryQueryData, CourseEnrolledQueryQueryVariables>(
    CourseEnrolledQuery,
    {
      variables: {
        id: courseId,
      },
      context: {
        clientName: 'gatewayGql',
      },
    }
  );

  const course = courseEnrolledData?.Course?.queryById;

  if (!course) {
    return null;
  }

  const isPreEnroll =
    // @ts-expect-error DateTime: string
    course.launchedAt >= Date.now() &&
    // @ts-expect-error DateTime: string
    course.preEnrollmentEnabledAt < Date.now() &&
    !course.isRestrictedMembership &&
    course.isReal;

  const link = path.join('/', 'learn', course.slug);
  const homeLink = path.join(link, 'home', 'welcome');

  let cta = isPreEnroll ? link : homeLink;

  if (isWeekLandingEnabled(courseId)) {
    cta = !isPreEnroll ? path.join(link, 'home', 'week', '1') : path.join(link, 'home');
  }

  const getWelcomeMessage = () => {
    if (isPreEnroll) {
      return _t('Welcome to the course. The course will start #{plannedDate}.', {
        plannedDate: course.plannedLaunchDate,
      });
    }

    const courseLabel = courseTypeMetadataWithVersion?.courseLabel;
    const isCourse = courseTypeMetadataWithVersion?.isCourse;

    if (courseLabel && !isCourse) {
      return _t('Welcome to the #{courseType}. You can now access the project materials.', { courseType: courseLabel });
    }

    return _t('Welcome to the course. You can now access the course materials.');
  };

  if (showCdsDesign) {
    return (
      <div>
        {children || false}
        <Typography2 component="div">{getWelcomeMessage()}</Typography2>
        <Button
          css={styles.actionContainer}
          component="a"
          href={cta}
          data-testid="course-enrollment-confirmation-start-learning-button"
        >
          {isPreEnroll ? _t('Continue') : _t('Start learning')}
        </Button>
      </div>
    );
  }

  return (
    <div className="rc-CourseEnrollmentConfirmation">
      {children || false}

      <div className="headline-1-text align-horizontal-center welcome-message">
        {getWelcomeMessage()}
        <br />
      </div>

      <br />

      <div className="align-horizontal-center">
        <a href={cta} className="styleguide nostyle start-learning-button-link">
          {/* eslint-disable react/button-has-type */}
          <button
            className="primary align-horizontal-center cozy"
            data-testid="course-enrollment-confirmation-start-learning-button"
          >
            {isPreEnroll ? _t('Continue') : _t('Start Learning')}
          </button>
          {/* eslint-enable react/button-has-type */}
        </a>
      </div>
    </div>
  );
};

export default CourseEnrollmentConfirmation;
